import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { ApiResponse } from "./types/response/ApiResponse";
import { User } from "../../types/User";
import { CreateUserRequest, ILoginRequest, ValidateCodeRequest } from "./types/requests/auth.requests.";
import { AdminLoginResponse } from "./types/response/auth.response";
import { Category } from "../../types/Category";
import { CategoryQuery, CreateCategoryPayload } from "./types/requests/category.request";
import { objectToQueryString } from "./utils/stringUtils";
import { QuestionQuery, UploadQuestionPayload } from "./types/requests/question.request";
import { Question } from "../../types/Question";
import { CreatePlayerPayload, PlayerQuery } from "./types/requests/player.request";
import { Player } from "../../types/Player";
import { CompetitionLeaderboardQuery, CompetitionQuery, CreateCompetitionPayload } from "./types/requests/competition.request";
import { Competition } from "../../types/Competitions";
import { CreateGameRoundPayload, FinishRoundPayload, InitiateRoundPayload, UpdateRoundPayload } from "./types/requests/game.request";
import { Round } from "../../types/Round";
import { GameRoundResponse } from "./types/response/game.response";
import { Leaderboard } from "../../types/Leaderboard";


export class MtcBackendClient {

    // Instance variables
    private _axiosInstance!: AxiosInstance;
    private cache: Map<string, { data: any; timestamp: number }> = new Map();
    public cacheTTL: number = 90 // 1:30 minute
    
    // private _token = process.env.TOKEN

    /**
     *
     */
    constructor() {
        this._axiosInstance = axios.create({
            baseURL: process.env.REACT_APP_BASE_URL
        });
    }

    public async authenticateUser(payload: ILoginRequest): Promise<ApiResponse<AdminLoginResponse>> {

        const cacheKey = `/authentication/login/`;

        let response = await this._axiosInstance.post<ApiResponse<AdminLoginResponse>>(cacheKey, payload);
        
        return response.data
    }

    public async createAdmin(payload: CreateUserRequest): Promise<ApiResponse<User>> {

        const cacheKey = `/user/admin`;

        let response = await this._axiosInstance.post<ApiResponse<User>>(cacheKey, payload);
        
        return response.data;
    }

    public async getAdminStatus(): Promise<ApiResponse<boolean>> {

        console.log(process.env.REACT_APP_BASE_URL);

        const cacheKey = `/user/admin/status`;

        let response = await this._axiosInstance.get<ApiResponse<boolean>>(cacheKey);
        
        return response.data;
    }


    public setToken(token: string, headers?: any){
        this._axiosInstance = axios.create({
            baseURL: process.env.REACT_APP_BASE_URL,
            headers:{
                ...headers,
                'Authorization': `Bearer ${token}`
            },
        });
    }

    public async fetchCategories(query?: CategoryQuery, cb?: (loading: boolean)=> void): Promise<ApiResponse<Category[]>>{

        let queryString = query ? objectToQueryString(query) : ''
        const cacheKey = `/category${queryString ? `?${queryString}` : ''}`;

        // First check cache
        const cachedData = this.cache.get(cacheKey);
        if (cachedData && Date.now() - cachedData.timestamp <= this.cacheTTL * 1000) {
            return cachedData.data;
        }

        cb && cb(true)

        let response = await this._axiosInstance.get<ApiResponse<Category[]>>(cacheKey);

        // Update the cache with the fresh data
        this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });

        cb && cb(false)

        return response.data
    }

    public async fetchCategoryById(id: number, cb?: (loading: boolean)=> void): Promise<ApiResponse<Category>>{

    
        const cacheKey = `/category/single/${id}`;

        // First check cache
        const cachedData = this.cache.get(cacheKey);
        if (cachedData && Date.now() - cachedData.timestamp <= this.cacheTTL * 1000) {
            return cachedData.data;
        }

        cb && cb(true)

        let response = await this._axiosInstance.get<ApiResponse<Category>>(cacheKey);

        // Update the cache with the fresh data
        this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });

        cb && cb(false)

        return response.data
    }

    public async createCategory(payload: CreateCategoryPayload): Promise<ApiResponse<Category>> {

        const cacheKey = `/category`;

        let response = await this._axiosInstance.post<ApiResponse<User>>(cacheKey, payload);

        // Reset cache
        this.cache.clear();
        
        return response.data;
    }

    public async fetchQuestions(query?: QuestionQuery, cb?: (loading: boolean)=> void): Promise<ApiResponse<Question[]>>{

        let queryString = query ? objectToQueryString(query) : ''
        const cacheKey = `/question${queryString ? `?${queryString}` : ''}`;

        // First check cache
        const cachedData = this.cache.get(cacheKey);
        if (cachedData && Date.now() - cachedData.timestamp <= this.cacheTTL * 1000) {
            return cachedData.data;
        }

        cb && cb(true)

        let response = await this._axiosInstance.get<ApiResponse<Question[]>>(cacheKey);

        // Update the cache with the fresh data
        this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });

        cb && cb(false)

        return response.data
    }

    public async uploadQuestion(payload: UploadQuestionPayload): Promise<ApiResponse> {

        const cacheKey = `/question/upload`;

        // config
        let config: AxiosRequestConfig = {
            headers:{
                'Content-Type': 'multipart/form-data'
            }
        }

        // Data
        const fdata = new FormData();
        fdata.append('file', payload.file);
        fdata.append('level', payload.level)
        

        let response = await this._axiosInstance.post<ApiResponse>(cacheKey, fdata, config);

        // Reset cache
        this.cache.clear();
        
        return response.data;
    }

    public async fetchPlayers(query?: PlayerQuery, cb?: (loading: boolean)=> void): Promise<ApiResponse<Player[]>>{

        let queryString = query ? objectToQueryString(query) : ''
        const cacheKey = `/player${queryString ? `?${queryString}` : ''}`;

        // First check cache
        const cachedData = this.cache.get(cacheKey);
        if (cachedData && Date.now() - cachedData.timestamp <= this.cacheTTL * 1000) {
            return cachedData.data;
        }

        cb && cb(true)

        let response = await this._axiosInstance.get<ApiResponse<Player[]>>(cacheKey);

        // Update the cache with the fresh data
        this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });

        cb && cb(false)

        return response.data
    }

    public async createPlayer(payload: CreatePlayerPayload): Promise<ApiResponse<Player>> {

        const cacheKey = `/player`;

        // config
        let config: AxiosRequestConfig = {
            headers:{
                'Content-Type': 'multipart/form-data'
            }
        }

        // Data
        const fdata = new FormData();
        fdata.append('image', payload.image);
        fdata.append('level', payload.level)
        fdata.append('firstname', payload.firstname)
        fdata.append('lastname', payload.lastname)
        fdata.append('age', payload.age.toString())
        fdata.append('grade', payload.grade)

        let response = await this._axiosInstance.post<ApiResponse<Player>>(cacheKey, payload, config);

        // Reset cache
        this.cache.clear();
        
        return response.data;
    }

    public async fetchCompetitions(query?: CompetitionQuery, cb?: (loading: boolean)=> void): Promise<ApiResponse<Competition[]>>{

        let queryString = query ? objectToQueryString(query) : ''
        const cacheKey = `/competition${queryString ? `?${queryString}` : ''}`;

        // First check cache
        const cachedData = this.cache.get(cacheKey);
        if (cachedData && Date.now() - cachedData.timestamp <= this.cacheTTL * 1000) {
            return cachedData.data;
        }

        cb && cb(true)

        let response = await this._axiosInstance.get<ApiResponse<Competition[]>>(cacheKey);

        // Update the cache with the fresh data
        this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });

        cb && cb(false)

        return response.data
    }

    public async fetchCompetitionById(id: number, query?: CompetitionQuery, cb?: (loading: boolean)=> void): Promise<ApiResponse<Competition>>{

        const queryString = query ? objectToQueryString(query) : ''
        const cacheKey = `/competition/single/${id}${queryString ? `?${queryString}` : ''}`;

        // First check cache
        const cachedData = this.cache.get(cacheKey);
        if (cachedData && Date.now() - cachedData.timestamp <= this.cacheTTL * 1000) {
            return cachedData.data;
        }

        cb && cb(true)

        let response = await this._axiosInstance.get<ApiResponse<Competition>>(cacheKey);

        // Update the cache with the fresh data
        this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });

        cb && cb(false)

        return response.data
    }

    public async createCompetition(payload: CreateCompetitionPayload): Promise<ApiResponse<Competition>> {

        const cacheKey = `/competition`;

        let response = await this._axiosInstance.post<ApiResponse<Competition>>(cacheKey, payload);

        // Reset cache
        this.cache.clear();
        
        return response.data;
    }

    public async initiateRound(payload: InitiateRoundPayload): Promise<ApiResponse<Round>> {

        const cacheKey = `/game/round/initiate`;

        let response = await this._axiosInstance.post<ApiResponse<Round>>(cacheKey, payload);

        // Reset cache
        // this.cache.clear();
        
        return response.data;
    }

    public async updateRound(payload: UpdateRoundPayload): Promise<ApiResponse<Round>> {

        const cacheKey = `/game/round/`;

        let response = await this._axiosInstance.patch<ApiResponse<Round>>(cacheKey, payload);

        // Reset cache
        // this.cache.clear();
        
        return response.data;
    }

    public async createGameRound(payload: UpdateRoundPayload): Promise<ApiResponse<GameRoundResponse>> {

        const cacheKey = `/game/round/start`;

        let response = await this._axiosInstance.post<ApiResponse<GameRoundResponse>>(cacheKey, payload);

        // Reset cache
        // this.cache.clear();
        
        return response.data;
    }

    public async finishGameRound(payload: FinishRoundPayload, file?: File): Promise<ApiResponse<Round>> {

        const cacheKey = `/game/round/finish`;

        // config
        let config: AxiosRequestConfig = {
            headers:{
                'Content-Type': 'multipart/form-data'
            }
        }

        // Data
        const fdata = new FormData();
        fdata.append('actualTime', payload.actualTime.toString());
        fdata.append('answeredCorrect', payload.answeredCorrect.toString())
        fdata.append('answeredWrong', payload.answeredWrong.toString())
        fdata.append('questionsAnswered', payload.questionsAnswered.toString())
        fdata.append('questionsSkipped', payload.questionsSkipped.toString())
        fdata.append('score', payload.score.toString())
        fdata.append('roundId', payload.roundId)
        fdata.append('filePath', payload.filePath)
        // fdata.append('file', file)

        let response = await this._axiosInstance.post<ApiResponse<Round>>(cacheKey, fdata, config);

        // Reset cache
        // this.cache.clear();
        
        return response.data;
    }

    public async fetchCompetitionLeaderboard(query: CompetitionLeaderboardQuery, cb?: (loading: boolean)=> void): Promise<ApiResponse<Leaderboard[]>>{

        let queryString = query ? objectToQueryString(query) : ''
        const cacheKey = `/competition/leaderboard${queryString ? `?${queryString}` : ''}`;

        // First check cache
        const cachedData = this.cache.get(cacheKey);
        if (cachedData && Date.now() - cachedData.timestamp <= this.cacheTTL * 1000) {
            return cachedData.data;
        }

        cb && cb(true)

        let response = await this._axiosInstance.get<ApiResponse<Leaderboard[]>>(cacheKey);

        // Update the cache with the fresh data
        this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });

        cb && cb(false)

        return response.data
    }

    public async validateCode(code: string): Promise<ApiResponse> {

        const cacheKey = `/authentication/validate/code`;

        let response = await this._axiosInstance.post<ApiResponse<ValidateCodeRequest>>(cacheKey, { code });

        // Reset cache
        this.cache.clear();
        
        return response.data;
    }

    


}

export const backendClient = new MtcBackendClient();
